import axios, { type AxiosError, type AxiosInstance } from 'axios';
import type { AuthAdapter } from '../auth/AuthAdapter';
import { AuthRefreshError } from '../auth/AuthRefreshError';
import { BffError, type BffAdapter, type BffRequestOptions } from './BffAdapter';
import { generateTraceparent } from '../../common/utils/traceparent';
import { createFetchSseClient, type SseSubscription } from '../../common/services/fetchSse';
import { isFrontdoorPath } from './frontdoorPaths';

export interface RestBffConfig {
  baseUrl: string;
  /**
   * DESO converse origin. When set, `/converse`, converse status SSE, and
   * `/style-preferences` use this base; everything else stays on `baseUrl`.
   * Omit to send all paths to `baseUrl` (uncut tenants).
   */
  converseUrl?: string;
  tenant?: string;
  tenantHeader?: string;
  authHeader?: string;
  authScheme?: string;
  defaultHeaders?: Record<string, string>;
  /** Header name for propagating user identity (user_ukey). Defaults to 'x-user-id'. */
  userHeader?: string;
}

function stripSlash(url: string): string {
  return url.replace(/\/$/, '');
}

export class RestBffAdapter implements BffAdapter {
  readonly name = 'rest';
  private readonly cfg: RestBffConfig;
  private readonly auth: AuthAdapter;
  private readonly opsClient: AxiosInstance;
  private readonly frontdoorClient: AxiosInstance;
  private forceLogoutHandler: (() => void) | null = null;

  constructor(config: RestBffConfig, authAdapter: AuthAdapter) {
    this.cfg = config;
    this.auth = authAdapter;
    const sharedHeaders = {
      'Content-Type': 'application/json',
      ...config.defaultHeaders,
    };
    this.opsClient = axios.create({
      baseURL: stripSlash(config.baseUrl),
      responseType: 'json',
      headers: sharedHeaders,
    });
    this.frontdoorClient = axios.create({
      baseURL: stripSlash(config.converseUrl ?? config.baseUrl),
      responseType: 'json',
      headers: sharedHeaders,
    });
  }

  setForceLogoutHandler(handler: () => void): void {
    this.forceLogoutHandler = handler;
  }

  private clientFor(path: string): AxiosInstance {
    return isFrontdoorPath(path) ? this.frontdoorClient : this.opsClient;
  }

  private buildUrl(path: string): string {
    const base = isFrontdoorPath(path)
      ? stripSlash(this.cfg.converseUrl ?? this.cfg.baseUrl)
      : stripSlash(this.cfg.baseUrl);
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  }

  private buildAuthHeader(token: string): string {
    const scheme = this.cfg.authScheme ?? 'Bearer';
    return scheme ? `${scheme} ${token}` : token;
  }

  /**
   * Remove any consumer header whose lowercased name matches a protected header name.
   * This prevents callers from spoofing Authorization, tenant, or user-identity headers.
   */
  private sanitizeConsumerHeaders(
    headers: Record<string, string> | undefined,
    protectedNames: Set<string>,
  ): Record<string, string> {
    if (!headers) return {};
    return Object.fromEntries(
      Object.entries(headers).filter(([name]) => !protectedNames.has(name.toLowerCase())),
    );
  }

  private buildHeaders(options: BffRequestOptions): Record<string, string> {
    const token = this.auth.getAccessToken();
    const userId = this.auth.getIdentity()?.user.id;
    const authHeader = this.cfg.authHeader ?? 'Authorization';
    const tenantHeader = this.cfg.tenantHeader ?? 'X-Tenant';
    const userHeader = this.cfg.userHeader ?? 'x-user-id';

    const protectedNames = new Set([authHeader, tenantHeader, userHeader].map(n => n.toLowerCase()));
    const sanitized = this.sanitizeConsumerHeaders(options.headers, protectedNames);

    return {
      ...sanitized,
      ...(token ? { [authHeader]: this.buildAuthHeader(token) } : {}),
      ...(this.cfg.tenant ? { [tenantHeader]: this.cfg.tenant } : {}),
      ...(userId ? { [userHeader]: userId } : {}),
      traceparent: generateTraceparent(),
    };
  }

  async request<T = unknown>(path: string, options: BffRequestOptions = {}): Promise<T> {
    const suffix = path.startsWith('/') ? path : `/${path}`;
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

    const executeRequest = async (): Promise<T> => {
      const response = await this.clientFor(path).request<T>({
        url: suffix,
        method: options.method ?? 'GET',
        headers: {
          ...this.buildHeaders(options),
          // Clear the instance-level Content-Type so axios can set
          // multipart/form-data with the correct boundary automatically.
          ...(isFormData ? { 'Content-Type': null } : {}),
        },
        data: options.body,
        signal: options.signal,
        timeout: options.timeout,
        ...(options.responseType ? { responseType: options.responseType } : {}),
      });

      if (response.status === 204) {
        return undefined as T;
      }

      return response.data;
    };

    try {
      return await executeRequest();
    } catch (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 401) {
        return this.handle401AndRetry(suffix, options, executeRequest);
      }

      return this.translateAxiosError(error);
    }
  }

  private async handle401AndRetry<T>(
    _path: string,
    _options: BffRequestOptions,
    executeRequest: () => Promise<T>,
  ): Promise<T> {
    try {
      await this.auth.refreshToken();
    } catch (err) {
      if (err instanceof AuthRefreshError) {
        // Only kill the session when the refresh endpoint itself says the
        // session is dead. Missing session_id / network blips must not log the
        // user out after a successful login when some BFF route returns 401.
        if (err.status === 401 || err.status === 403) {
          console.error('[bff] token refresh failed — forcing logout', err.message);
          this.forceLogoutHandler?.();
        } else {
          console.error('[bff] token refresh failed — not forcing logout', err.message);
        }
        throw new BffError(401, 'Unauthorized', null);
      }
      throw err;
    }

    try {
      return await executeRequest();
    } catch (retryError) {
      const axiosErr = retryError as AxiosError;
      if (axiosErr.response?.status === 401) {
        // Refresh succeeded, so the session is still valid. A second 401 is
        // resource denial / misconfig — surface the error, keep the session.
        console.error('[bff] retry also returned 401 — not forcing logout');
        throw new BffError(401, 'Unauthorized', null);
      }
      return this.translateAxiosError(retryError);
    }
  }

  private translateAxiosError(error: unknown): never {
    const axiosError = error as AxiosError;

    if (axiosError.response) {
      throw new BffError(
        axiosError.response.status,
        axiosError.response.statusText,
        axiosError.response.data ?? null,
      );
    }

    if (axiosError.code === 'ECONNABORTED') {
      throw new BffError(0, 'ECONNABORTED', null);
    }

    if (axiosError.request) {
      throw new TypeError(axiosError.message, { cause: axiosError });
    }

    throw error;
  }

  openEventStream(path: string): SseSubscription {
    return createFetchSseClient(this.buildUrl(path), {
      getBearer: async () => this.auth.getAccessToken() ?? '',
    });
  }
}
